import { odooApi } from '../index';
import { Department, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { extractErrorMessage } from '../transforms';

/**
 * Departments API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all department-related API endpoints.
 * Connected to real Odoo API at /api/moys/departments
 */

// Helper function for fallback department lookup
async function fallbackToAllDepartments(
  id: string,
  _lang: 'ar' | 'en' | 'ku',
  baseQuery: any,
  odooLang: string
): Promise<{ data?: Department; error?: any }> {
  try {
    const result = await baseQuery({
      url: `/api/moys/departments?lang=${odooLang}`,
      method: 'POST',
    });
    
    if (result.error) {
      return { error: result.error as any };
    }
    
    const response = result.data as any;
    
    if (!response || !response.success || !response.data || !Array.isArray(response.data)) {
      return { error: { status: 500, data: 'Invalid response format' } as any };
    }
    
    const departments = response.data.map((item: any, index: number) => {
      // Extract photos from slider_images array (backend changed from media.images to slider_images)
      const photos = item.slider_images?.map((img: any) => img.image).filter(Boolean) || 
                    item.media?.images?.map((img: any) => img.file_url) || 
                    item.images?.map((img: any) => (typeof img === 'string' ? img : img.file_url || img.url)) || 
                    item.photos || 
                    [];
      
      const videos = item.media?.videos?.map((vid: any) => vid.video_url) || 
                    item.videos?.map((vid: any) => (typeof vid === 'string' ? vid : vid.video_url || vid.url)) || 
                    [];
      
      // Ensure IDs start from 1: if id is 0, null, undefined, or falsy, use index + 1
      const departmentId = (item.id != null && item.id !== 0) ? item.id : (index + 1);
      
      return {
        id: String(departmentId || item.code || (index + 1)),
        slug: item.website_slug || item.code || String(departmentId || (index + 1)),
        nameAr: item.name || '',
        nameEn: item.name || '',
        nameKu: item.name || '',
        parentDepartment: item.parent_department
          ? {
              id: String(item.parent_department.id || ''),
              nameAr: item.parent_department.name || '',
              nameEn: item.parent_department.name || '',
              nameKu: item.parent_department.name || '',
            }
          : null,
        description: {
          ar: item.description || '',
          en: item.description || '',
          ku: item.description || '',
        },
        contacts: {
          email: item.email || '',
          phone: item.mobile || item.phone || '',
          address: {
            ar: item.address || '',
            en: item.address || '',
            ku: item.address || '',
          },
        },
        bannerImage: item.image || item.banner_image || undefined,
        photos: photos.length > 0 ? photos : undefined,
        videos: videos.length > 0 ? videos : undefined,
        featured: item.featured || false,
        managerName: item.manager_name || item.manager || undefined,
        createdAt: item.create_date || new Date().toISOString(),
      };
    });
    
    const searchId = String(id).toLowerCase().trim();
    const department = departments.find((d: Department) => {
      const deptId = String(d.id || '').toLowerCase().trim();
      // Match exact ID or try numeric comparison if both are numeric
      if (deptId === searchId) {
        return true;
      }
      // Also try matching the numeric ID if searchId is numeric
      const searchNum = parseInt(searchId, 10);
      const deptNum = parseInt(deptId, 10);
      if (!isNaN(searchNum) && !isNaN(deptNum) && searchNum === deptNum) {
        return true;
      }
      return false;
    });
    
    if (department) {
      console.log('[Departments API] Found department via fallback:', department.id);
      return { data: department as Department };
    }
    
    return { error: { status: 404, data: `Department not found: ${id}` } as any };
  } catch (error) {
    return { error: { status: 500, data: extractErrorMessage(error) } as any };
  }
}

export const departmentsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all departments from Odoo
    getDepartments: builder.query<
      PaginatedResponse<Department>,
      { page?: number; pageSize?: number; lang?: 'ar' | 'en' | 'ku' }
    >({
      query: ({ page = 1, pageSize = 50, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });

        return {
          url: `/api/moys/departments?${params.toString()}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any, _meta, arg) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }

          const departments = response.data.map((item: any, index: number) => {
            // Extract photos from slider_images array (backend changed from media.images to slider_images)
            const photos = item.slider_images?.map((img: any) => img.image).filter(Boolean) || 
                          item.media?.images?.map((img: any) => img.file_url) || 
                          item.images?.map((img: any) => (typeof img === 'string' ? img : img.file_url || img.url)) || 
                          item.photos || 
                          [];
            
            // Extract videos from media.videos array
            const videos = item.media?.videos?.map((vid: any) => vid.video_url) || 
                          item.videos?.map((vid: any) => (typeof vid === 'string' ? vid : vid.video_url || vid.url)) || 
                          [];

            // Ensure IDs start from 1: if id is 0, null, undefined, or falsy, use index + 1
            const departmentId = (item.id != null && item.id !== 0) ? item.id : (index + 1);

            return {
              id: String(departmentId || item.code || (index + 1)),
              slug: item.website_slug || item.code || String(departmentId || (index + 1)),
              nameAr: item.name || '',
              nameEn: item.name || '',
              nameKu: item.name || '',
              parentDepartment: item.parent_department
                ? {
                    id: String(item.parent_department.id || ''),
                    nameAr: item.parent_department.name || '',
                    nameEn: item.parent_department.name || '',
                    nameKu: item.parent_department.name || '',
                  }
                : null,
              description: {
                ar: item.description || '',
                en: item.description || '',
                ku: item.description || '',
              },
              contacts: {
                email: item.email || '',
                phone: item.mobile || item.phone || '',
                address: {
                  ar: item.address || '',
                  en: item.address || '',
                  ku: item.address || '',
                },
              },
              bannerImage: item.image || item.banner_image || undefined,
              photos: photos.length > 0 ? photos : undefined,
              videos: videos.length > 0 ? videos : undefined,
              featured: item.featured || false,
              managerName: item.manager_name || item.manager || undefined,
              createdAt: item.create_date || new Date().toISOString(),
            };
          }) as Department[];

          const pagination = response.pagination || {};
          const totalRecords =
            pagination.total_records ??
            pagination.totalRecords ??
            response.total_records ??
            departments.length;
          const pageSize =
            pagination.page_size ?? pagination.pageSize ?? arg.pageSize ?? departments.length;
          const currentPage =
            pagination.current_page ?? pagination.currentPage ?? arg.page ?? 1;
          const totalPages =
            pagination.total_pages ??
            pagination.totalPages ??
            Math.max(1, Math.ceil(totalRecords / pageSize));

          return {
            data: departments,
            total: totalRecords,
            page: currentPage,
            pageSize,
            totalPages,
          } as PaginatedResponse<Department>;
        } catch (error) {
          console.error('[Departments API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) => {
        if (!result) {
          return [{ type: 'Department', id: 'LIST' }];
        }
        
        // result is PaginatedResponse<Department>, so result.data is the array
        if (result.data && Array.isArray(result.data)) {
          return [
            ...result.data.map((dept) => ({ type: 'Department' as const, id: dept.id })),
            { type: 'Department', id: 'LIST' },
          ];
        }
        
        return [{ type: 'Department', id: 'LIST' }];
      },
    }),

    // Get single department by ID
    getDepartment: builder.query<Department, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, _api, _extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);
        
        try {
          // First, try to use the ID directly if it's numeric (backend expects integer)
          let departmentId = parseInt(id, 10);
          
          // If ID is numeric, try the direct endpoint first
          if (!isNaN(departmentId)) {
            const result = await baseQuery({
              url: `/api/moys/departments/${departmentId}?lang=${odooLang}`,
              method: 'GET',
            });
            
            if (!result.error && result.data) {
              const response = result.data as any;
              if (response && response.success && response.data) {
                // Successfully got department via ID endpoint
                const item = response.data;
                const photos = item.slider_images?.map((img: any) => img.image).filter(Boolean) || [];
                const videos = item.media?.videos?.map((vid: any) => vid.video_url) || [];
                
                const department: Department = {
                  id: String(item.id || item.code || id),
                  slug: item.website_slug || item.code || String(item.id || ''),
                  nameAr: item.name || '',
                  nameEn: item.name || '',
                  nameKu: item.name || '',
                  parentDepartment: item.parent_department
                    ? {
                        id: String(item.parent_department.id || ''),
                        nameAr: item.parent_department.name || '',
                        nameEn: item.parent_department.name || '',
                        nameKu: item.parent_department.name || '',
                      }
                    : null,
                  description: {
                    ar: item.description || '',
                    en: item.description || '',
                    ku: item.description || '',
                  },
                  contacts: {
                    email: item.email || '',
                    phone: item.mobile || item.phone || '',
                    address: {
                      ar: item.address || '',
                      en: item.address || '',
                      ku: item.address || '',
                    },
                  },
                  bannerImage: item.image || undefined,
                  photos: photos.length > 0 ? photos : undefined,
                  videos: videos.length > 0 ? videos : undefined,
                  featured: false,
                  managerName: undefined,
                  createdAt: new Date().toISOString(),
                };
                
                console.log('[Departments API] Found department via ID endpoint:', department.id);
                return { data: department };
              }
            }
            
            // If result has error, fallback
            if (result.error) {
              console.error('[Departments API] Department endpoint error:', result.error);
              // Fallback to fetching all departments and filtering
              return await fallbackToAllDepartments(id, lang, baseQuery, odooLang);
            }
          }
          
          // If direct endpoint failed or ID is not numeric, use fallback lookup
          console.warn('[Departments API] Using fallback lookup for ID:', id);
          return await fallbackToAllDepartments(id, lang, baseQuery, odooLang);
        } catch (error) {
          console.error('[Departments API] Get single department error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: 'Department', id }],
    }),
  }),
});

export const {
  useGetDepartmentsQuery,
  useGetDepartmentQuery,
} = departmentsApi;
